import { HttpResponse, HttpStatusEnum } from '@core/api';
import { AimPredefinedProbe } from '@features/aim/aim-details/_core/types/aim-predefined-probe.interface';
import { Probe } from '@features/strain/strain-details/_core/types';
import { FormatterHelper } from '@shared/helpers';
import { TextValue } from '@shared/types';

function mapAutosuggestUsers(data: any): HttpResponse<{ id: number; name: string; email: string }[]> {
    return {
        isLoading: false,
        status: HttpStatusEnum.SUCCESS,
        data: data.users.map((user: any) => ({
            id: user.id,
            name: FormatterHelper.computeUserFullName(user.firstName, user.lastName),
            email: user.email,
        })),
    };
}

function mapAutosuggestOrganizations(data: any): HttpResponse<{ id: number; name: string }[]> {
    return {
        isLoading: false,
        status: HttpStatusEnum.SUCCESS,
        data: data.organizations.map((organization: any) => ({
            id: organization.id,
            name: organization.name,
        })),
    };
}

function mapAutosuggestLaboratories(data: any): HttpResponse<{ id: number; name: string }[]> {
    return {
        isLoading: false,
        status: HttpStatusEnum.SUCCESS,
        data: data.laboratories.map((laboratory: any) => ({
            id: laboratory.id,
            name: laboratory.name,
        })),
    };
}

function mapAutosuggestAssayDesigners(
    data: any
): HttpResponse<{ id: number; email: string; name: string; isAvailable: boolean }[]> {
    return {
        isLoading: false,
        status: HttpStatusEnum.SUCCESS,
        data: data.assayDesigners.map((assayDesigner: any) => ({
            id: assayDesigner.id,
            email: assayDesigner.email,
            name: FormatterHelper.computeUserFullName(assayDesigner.firstName, assayDesigner.lastName),
            isAvailable: assayDesigner.isAvailable,
        })),
    };
}

function mapSuggestUserPredefinedProbesForStrains(data: any): HttpResponse<{ probes: Probe[] }> {
    return {
        isLoading: false,
        status: HttpStatusEnum.SUCCESS,
        data: {
            probes: (data.userPredefinedProbes || []).map((probe: any) => {
                return {
                    predefinedProbeId: probe.predefinedProbeId,
                    userPredefinedProbeId: probe.userPredefinedProbeId,
                    name: probe.userPredefinedProbeName,
                    predefinedProbeName: probe.predefinedProbeName,
                    probeStatus: probe.probeStatus,
                };
            }),
        },
    };
}

function mapSuggestPredefinedProbes(data: any): HttpResponse<AimPredefinedProbe[]> {
    return {
        isLoading: false,
        status: HttpStatusEnum.SUCCESS,
        data: (data.predefinedProbes || []).map((probe: any) => {
            return {
                predefinedProbeId: probe.predefinedProbeId,
                predefinedProbeName: probe.predefinedProbeName,
                zygosity: probe.zygosity,
                plexing: probe.plexing,
                showResultsEntryNote: probe.showResultsEntryNote,
                resultsEntryNote: probe.resultsEntryNote,
            };
        }),
    };
}

function mapSuggestPredefinedProbesForIncompatibility(data: any): HttpResponse<TextValue[]> {
    return {
        isLoading: false,
        status: HttpStatusEnum.SUCCESS,
        data: (data.predefinedProbes || []).map((probe: any) => {
            return {
                text: probe.text,
                value: probe.value,
            };
        }),
    };
}

function mapSuggestStrains(data: any): HttpResponse<{ id: number; name: string }[]> {
    return {
        isLoading: false,
        status: HttpStatusEnum.SUCCESS,
        data: data.strains.map((strain: any) => ({
            id: strain.strain.id,
            name: strain.strain.name,
        })),
    };
}

function mapSuggestIncompatibleProbes(data: any): HttpResponse<TextValue[]> {
    return {
        isLoading: false,
        status: HttpStatusEnum.SUCCESS,
        data: data.incompatibleProbes.map((probe: any) => ({
            text: probe.text,
            value: probe.value,
        })),
    };
}

function mapSuggestIncompatibleGroupNames(data: any): HttpResponse<TextValue[]> {
    return {
        isLoading: false,
        status: HttpStatusEnum.SUCCESS,
        data: (data.groups || []).map((groupName: any) => ({
            text: groupName.text,
            value: groupName.value,
        })),
    };
}

export const AutosuggestMapper = {
    mapAutosuggestUsers,
    mapAutosuggestOrganizations,
    mapAutosuggestLaboratories,
    mapAutosuggestAssayDesigners,
    mapSuggestUserPredefinedProbesForStrains,
    mapSuggestPredefinedProbes,
    mapSuggestStrains,
    mapSuggestPredefinedProbesForIncompatibility,
    mapSuggestIncompatibleProbes,
    mapSuggestIncompatibleGroupNames,
};
